import { useState } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';
import { GALLERY_TYPE_ICON } from '@/data/categories';
import { inlineVideo } from '@/lib/media';
import type { GalleryItem } from '@/types';
import styles from '@/sections/Gallery.module.css';

export interface GalleryCardProps {
  readonly item: GalleryItem;
  readonly onRemove: (id: string) => void;
}

export function GalleryCard({ item, onRemove }: GalleryCardProps) {
  // Thumbnails are visitor-supplied URLs that can rot at any time. Falling back
  // to the type icon keeps the card intact instead of showing a broken image.
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);

  const clip = inlineVideo(item.url);
  const showThumbnail = !clip && Boolean(item.thumbnail) && !thumbnailFailed;

  // Three shapes of media box: a clip is sized by the clip itself, a thumbnail
  // gets a fixed crop because its dimensions are unknown until it arrives, and
  // a post with neither keeps a short strip holding the type icon.
  const mediaShape = clip
    ? styles.mediaVideo
    : showThumbnail
      ? styles.mediaImage
      : styles.mediaIcon;

  return (
    <article className={styles.card}>
      <header className={styles.postHead}>
        <span className={styles.type}>{item.type}</span>

        {!item.seed ? (
          <Button
            variant="subtle"
            className={styles.remove}
            onClick={() => onRemove(item.id)}
            aria-label={`حذف ${item.title}`}
          >
            حذف
          </Button>
        ) : null}
      </header>

      <div className={`${styles.media} ${mediaShape}`}>
        {/* The icon is always painted and the thumbnail layers over it once it
            arrives. Swapping between them instead would leave an empty grey box
            for the whole request — and with `loading="lazy"` that is the normal
            state of every post below the fold, indefinitely so for a URL that
            hangs without ever firing `error`.

            A clip needs no such stand-in: its poster ships with the site and
            arrives with the page. */}
        {clip ? null : <Icon name={GALLERY_TYPE_ICON[item.type]} size={30} />}

        {/* Sources run best-first: a browser takes the first one it can play,
            so VP9 reaches everything current and the H.264 file is left for
            what cannot.

            `width`/`height` give the post its shape before anything loads, and
            `preload="none"` keeps the clip off the wire until someone presses
            play: the poster is a few kilobytes, the file is megabytes, and most
            visitors scroll past. */}
        {clip ? (
          // No captions track yet: the clip has Arabic narration and nobody has
          // written the transcript. Drop a .vtt beside the file and add
          // <track kind="captions" srcLang="ar"> here, then remove this line.
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            className={styles.video}
            poster={item.thumbnail || undefined}
            width={clip.width}
            height={clip.height}
            controls
            preload="none"
            playsInline
            aria-label={item.title}
          >
            {clip.sources.map((source) => (
              <source key={source.src} src={source.src} type={source.type} />
            ))}
          </video>
        ) : null}

        {showThumbnail ? (
          <img
            className={`${styles.thumbnail} ${thumbnailLoaded ? styles.thumbnailReady : ''}`}
            src={item.thumbnail}
            alt=""
            loading="lazy"
            decoding="async"
            onLoad={() => setThumbnailLoaded(true)}
            onError={() => setThumbnailFailed(true)}
          />
        ) : null}
      </div>

      <div className={styles.content}>
        <h4 className={styles.title}>{item.title}</h4>
        {item.description ? <p className={styles.description}>{item.description}</p> : null}

        <div className={styles.footer}>
          <a className={styles.link} href={item.url} target="_blank" rel="noopener noreferrer">
            فتح المحتوى
            <span className="visually-hidden"> — {item.title} (يفتح في نافذة جديدة)</span>
          </a>
        </div>
      </div>
    </article>
  );
}
